import { useTranslation } from 'react-i18next';
import {
  Button,
  EmptyState,
  ErrorState,
  Icon,
  LoadingState,
  WorkspaceTable,
  type WorkspaceTableColumn,
} from '../../../ui/index.js';
import type { RequestsBatchExportController } from '../selection/useRequestSelection.js';
import type { RequestView } from '../types.js';
import { ClassicRequestListRow } from './ClassicRequestListRow.js';

const GRID_TEMPLATE = '110px minmax(240px,1fr) 110px 100px 92px 96px 96px 204px 122px 98px';
const SELECTION_GRID_TEMPLATE = `40px ${GRID_TEMPLATE}`;

// WHY: `WorkspaceTable` renders its children straight into a `display: grid`,
// so a state wrapper without the span collapses into the first column track.
const STATE_SPAN = { gridColumn: '1 / -1' } as const;

// WHY: this role-scoped view is frozen at the pre-#2750 desktop layout for
// `logist_cn`; future list features land in the default view only.
export function ClassicRequestsListTable(props: {
  visible: RequestView[];
  now: number;
  selectedId: string | null;
  isManagerValidating: boolean;
  isError: boolean;
  isInitialLoading: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onOpenRow: (request: RequestView) => void;
  onLoadMore: () => void;
  unreadFor: (id: string) => { unreadCount: number; isMarkedUnread: boolean };
  notificationCountFor: (id: string) => number;
  batchExport?: Pick<
    RequestsBatchExportController,
    'selectionMode' | 'selectedRequestIds' | 'toggle'
  >;
}) {
  const { t } = useTranslation(['requests', 'common']);
  const {
    visible,
    now,
    selectedId,
    isManagerValidating,
    isError,
    isInitialLoading,
    hasNextPage,
    isFetchingNextPage,
    onOpenRow,
    onLoadMore,
    unreadFor,
    notificationCountFor,
    batchExport,
  } = props;
  const selectionMode = batchExport?.selectionMode ?? false;
  const selectedRequestIds = batchExport?.selectedRequestIds ?? new Set<string>();
  const dataColumns: readonly WorkspaceTableColumn[] = [
    { key: 'id', label: 'ID' },
    { key: 'name', label: t('requests:list.columns.name') },
    { key: 'status', label: t('requests:list.columns.status') },
    { key: 'answers', label: t('list.columns.answers') },
    { key: 'weight', label: t('list.columns.weight'), align: 'right' },
    { key: 'volume', label: t('list.columns.volume'), align: 'right' },
    { key: 'density', label: t('list.columns.density'), align: 'right' },
    { key: 'deadlines', label: t('list.columns.deadlines') },
    { key: 'manager', label: t('list.columns.manager') },
    { key: 'updated', label: t('list.columns.updated') },
  ];
  const columns: readonly WorkspaceTableColumn[] = selectionMode
    ? [
        {
          key: 'selection',
          label: (
            <span
              role="img"
              aria-label={t('requests:export.bulk.selectColumn')}
              title={t('requests:export.bulk.selectColumn')}
            >
              <Icon name="check" size={12} />
            </span>
          ),
        },
        ...dataColumns,
      ]
    : dataColumns;

  return (
    <div
      data-testid="classic-requests-list-table"
      style={{ flex: 1, overflow: 'auto', minHeight: 0 }}
    >
      <WorkspaceTable
        columnsTemplate={selectionMode ? SELECTION_GRID_TEMPLATE : GRID_TEMPLATE}
        columns={columns}
      >
        {visible.map((request) => (
          <ClassicRequestListRow
            key={request.id}
            r={request}
            now={now}
            selected={
              selectionMode ? selectedRequestIds.has(request.id) : request.id === selectedId
            }
            selectionMode={selectionMode}
            selectionChecked={selectedRequestIds.has(request.id)}
            onSelect={() =>
              selectionMode ? batchExport?.toggle(request, false) : onOpenRow(request)
            }
            onToggleSelection={(shiftKey) => batchExport?.toggle(request, shiftKey)}
            unread={unreadFor(request.id)}
            notificationCount={notificationCountFor(request.id)}
          />
        ))}

        {visible.length === 0 && isError && (
          <div data-testid="requests-list-error" role="alert" style={STATE_SPAN}>
            <ErrorState title={t('requests:list.error')} />
          </div>
        )}
        {visible.length === 0 && !isError && (isManagerValidating || isInitialLoading) && (
          <div data-testid="requests-list-loading" style={STATE_SPAN}>
            <LoadingState text={t('requests:list.loading')} />
          </div>
        )}
        {visible.length === 0 && !isError && !isManagerValidating && !isInitialLoading && (
          <div data-testid="requests-list-empty" style={STATE_SPAN}>
            <EmptyState icon="inbox" title={t('requests:list.empty')} />
          </div>
        )}
      </WorkspaceTable>

      {hasNextPage && (
        <div style={{ padding: '14px 24px 22px', display: 'flex', justifyContent: 'center' }}>
          <Button
            kind="secondary"
            size="sm"
            data-testid="requests-load-more"
            disabled={isFetchingNextPage}
            onClick={onLoadMore}
          >
            {isFetchingNextPage ? t('requests:list.loadingMore') : t('requests:list.loadMore')}
          </Button>
        </div>
      )}
    </div>
  );
}
